import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";

export default function EmailPhotos() {
  const nav = useNavigate();
  const [form, setForm] = useState({
    recipient_email: "", subject: "A few photographs to remember by", note: "", deceased_name: "",
  });
  const [photos, setPhotos] = useState([]); // base64 list
  const [busy, setBusy] = useState(false);

  const onUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    const readers = files.map(f => new Promise((res, rej) => {
      const r = new FileReader();
      r.onload = () => res(String(r.result).split(",")[1]);
      r.onerror = rej;
      r.readAsDataURL(f);
    }));
    const results = await Promise.all(readers);
    setPhotos([...photos, ...results]);
  };

  const remove = (i) => setPhotos(photos.filter((_, idx) => idx !== i));

  const send = async (e) => {
    e.preventDefault();
    if (!photos.length) { toast.error("Add at least one photo."); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/photos/email", {
        recipient_email: form.recipient_email,
        subject: form.subject || "A few photographs to remember by",
        note: form.note,
        deceased_name: form.deceased_name,
        photos,
      });
      if (data.sent) {
        toast.success(`Sent (${data.count} photo${data.count > 1 ? "s" : ""}).`);
        nav("/dashboard");
      } else {
        toast.error("Email could not be delivered. Please try again.");
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Send failed.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={{ maxWidth: 720, margin: "30px auto", padding: "0 24px 60px" }}>
      <div style={{ marginBottom: 24 }}>
        <div style={{ fontSize: 11, letterSpacing: "0.3em", color: "var(--gold)", marginBottom: 6 }}>EMAIL PHOTOS</div>
        <h1 className="serif" style={{ fontSize: 38, color: "#fff", margin: 0, fontWeight: 400 }}>Share photographs</h1>
        <p className="muted" style={{ marginTop: 8, fontSize: 14 }}>
          Email photos directly to someone — no obituary required.
        </p>
      </div>

      <form onSubmit={send} className="bg-card" style={{ padding: 28 }}>
        <label className="label-dark">Recipient email *</label>
        <input data-testid="ep-recipient" required type="email" value={form.recipient_email} onChange={e => setForm({ ...form, recipient_email: e.target.value })} className="input-dark" placeholder="recipient@email.com" style={{ marginBottom: 16 }} />

        <label className="label-dark">In memory of (optional)</label>
        <input data-testid="ep-deceased" value={form.deceased_name} onChange={e => setForm({ ...form, deceased_name: e.target.value })} className="input-dark" placeholder="e.g., Margaret Anne Thompson" style={{ marginBottom: 16 }} />

        <label className="label-dark">Subject</label>
        <input data-testid="ep-subject" value={form.subject} onChange={e => setForm({ ...form, subject: e.target.value })} className="input-dark" style={{ marginBottom: 16 }} />

        <label className="label-dark">Message (optional)</label>
        <textarea data-testid="ep-note" rows={4} value={form.note} onChange={e => setForm({ ...form, note: e.target.value })} className="input-dark" placeholder="A few photos to remember her by — with love." style={{ resize: "vertical", marginBottom: 18 }} />

        <label className="label-dark">Photographs ({photos.length})</label>
        <input data-testid="ep-upload" type="file" accept="image/*" multiple onChange={onUpload} className="input-dark" style={{ marginBottom: 14 }} />
        {photos.length > 0 && (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(110px, 1fr))", gap: 10, marginBottom: 16 }}>
            {photos.map((p, i) => (
              <div key={i} style={{ position: "relative" }}>
                <img src={`data:image/jpeg;base64,${p}`} alt={`photo-${i}`} style={{ width: "100%", height: 110, objectFit: "cover", borderRadius: 8, border: "1px solid var(--border)" }} />
                <button type="button" onClick={() => remove(i)} aria-label="Remove" style={{ position: "absolute", top: 4, right: 4, background: "rgba(0,0,0,0.7)", color: "#fff", border: "none", borderRadius: 999, width: 24, height: 24, cursor: "pointer" }}>×</button>
              </div>
            ))}
          </div>
        )}

        <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginTop: 12 }}>
          <button type="button" onClick={() => nav(-1)} className="btn-ghost">← Back</button>
          <button data-testid="ep-send" type="submit" disabled={busy} className="btn-gold" style={{ flex: 1, minWidth: 180 }}>{busy ? "Sending…" : "Send Photos →"}</button>
        </div>
      </form>
    </div>
  );
}
